import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CheckboxLabel, FooterStyled, FormSection } from './PersonalDetails.styles'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Checkbox, Input } from '@mobi/component-library/Selection&Input/Inputs'
import { getSignUpState, setSignupApplication, setSignupVerification } from '../Store'
import { REQUIRED_VALIDATION_MESSAGE, routes } from '../Utils'
import { isEmailValid, isMobileValid } from '@mobi/account/Utils'
import { Button } from '@mobi/component-library/Base/Buttons'
import { useApplication, useSignUpApplication } from '../Hooks'
import { SignupError } from './SignupError'
import { signupTracker } from '@mobi/account/Common/Analytics'
import dayjs from 'dayjs'
import { useLocation, useNavigate } from 'react-router'
import {
  duplicateEmailErrorMessage,
  duplicateMobileErrorMessage,
} from '../../ContactDetails/Utils/formValidation'
import { SignUpPageStyled } from './SignUpPage.styled'
import { StepGroup } from '@mobi/component-library/Base/StepGroup'

export const ContactDetails = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const state = useSelector(getSignUpState)
  const application = state.signupApplication
  const verification = state.signupVerification
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})

  const { saveSignUpApplication, saveSignUpApplicationStatus } = useSignUpApplication({
    setValidationMessages,
    setShowErrorPopup,
  })
  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
    nextPageRoute: routes.additionalInformation,
  })

  const isBusy = saveSignUpApplicationStatus === 'pending' || applicationSaveStatus === 'pending'

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  function getMobileValidationMessage(enteredText: string) {
    if (enteredText === '') return REQUIRED_VALIDATION_MESSAGE

    if (!isMobileValid(enteredText)) {
      return 'Please enter a valid Australian mobile number.'
    }

    return ''
  }

  function getEmailValidationMessage(enteredText: string) {
    if (enteredText === '') return REQUIRED_VALIDATION_MESSAGE

    if (!isEmailValid(enteredText)) {
      return 'Please enter a proper email address.'
    }

    return ''
  }

  function useQuery() {
    const params = new URLSearchParams()
    new URLSearchParams(useLocation().search).forEach((value, name) => {
      params.append(name.toLowerCase(), value)
    })
    return params
  }

  const queries = useQuery()

  const saveApplication = () => {
    if (!application.applicationReferenceNumber) {
      const signupApplication = {
        firstName: application.firstName,
        middleName: application.middleName,
        surname: application.surname,
        birthdate: dayjs(application.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        email: application.email,
        mobileNumber: application.mobileNumber,
        applicationReferenceNumber: application.applicationReferenceNumber,
        enquiryId: application.enquiryId,
        residentialAddress: application.residentialAddress,
        previousAddress: application.previousAddress,
        marketingOptInIndicator: application.marketingOptInIndicator,
        affiliateMarketingId: queries.get('affiliateid') as string,
        locationId: queries.get('locationid') as string,
        venueId: queries.get('venueid') as string,
        firstNameAlias: application.firstNameAlias,
        lastNameAlias: application.lastNameAlias,
      }
      saveSignUpApplication(signupApplication, {
        onSuccess: response => {
          signupTracker({
            interaction_type: 'Contact Details',
            signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
            marketingConsent: application.marketingOptInIndicator ? true : false,
          })

          dispatch(
            setSignupApplication({
              ...application,
              applicationReferenceNumber: Number(response.applicationReferenceNumber),
            })
          )
          dispatch(
            setSignupVerification({
              ...verification,
              duplicateExists: response.duplicateExists,
              isDuplicateClosed: response.isDuplicateClosed,
            })
          )
          if (response.duplicateExists) {
            navigate(routes.signUpUnsuccessful)
            return
          }
          navigate(routes.additionalInformation)
        },
      })
    } else {
      applicationSave(
        {},
        {
          onSuccess: () => {
            signupTracker({
              interaction_type: 'Contact Details',
              signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
              marketingConsent: application.marketingOptInIndicator ? true : false,
            })
          },
        }
      )
    }
  }
  return (
    <>
      <SignUpPageStyled>
        <StepGroup currentStep={2} totalSteps={5} />
        <AccountHeader alignItems='start' title='Contact details' style={{ marginBottom: 0 }} />
        <FormSection>
          <Input
            type='tel'
            label='Mobile'
            name='mobile'
            placeholder='Enter a valid mobile number'
            required
            maxLength={10}
            value={application.mobileNumber}
            onChange={e =>
              dispatch(
                setSignupApplication({
                  ...application,
                  mobileNumber: e.target.value.replace(/[\D]/gi, ''),
                })
              )
            }
            onFocus={() => resetFieldValidation('mobile')}
            onBlur={e => {
              const trimmedValue = e.target.value.trim()
              setValidationMessages(prev => ({
                ...prev,
                mobile: getMobileValidationMessage(trimmedValue),
              }))
            }}
            isInvalid={!!validationMessages.mobile}
            helpText={
              validationMessages.mobile === 'duplicate'
                ? duplicateMobileErrorMessage
                : validationMessages.mobile
            }
            isDisabled={isBusy}
          />

          <Input
            type='email'
            label='Email'
            name='email'
            placeholder='Enter a valid email address'
            required
            maxLength={50}
            value={application.email}
            onChange={e =>
              dispatch(
                setSignupApplication({
                  ...application,
                  email: e.target.value,
                })
              )
            }
            onFocus={() => resetFieldValidation('email')}
            onBlur={e => {
              const trimmedValue = e.target.value.trim()
              setValidationMessages(prev => ({
                ...prev,
                email: getEmailValidationMessage(trimmedValue),
              }))
            }}
            isInvalid={!!validationMessages.email}
            helpText={
              validationMessages.email === 'duplicate'
                ? duplicateEmailErrorMessage
                : validationMessages.email
            }
            isDisabled={isBusy}
          />

          {!state?.isVerificationAccount && (
            <div>
              <Checkbox
                id='sign-up-marketing-subscription'
                name='MarketingOptIn'
                checked={application.marketingOptInIndicator}
                label={
                  <CheckboxLabel>
                    I consent to receive advertising and promotional material by email, post, SMS,
                    telephone and other direct means in line with TABtouch’s{' '}
                    <a
                      href='https://www.rwwa.com.au/privacy-policy/'
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      Privacy Policy
                    </a>
                  </CheckboxLabel>
                }
                data-tid-sign-up-marketing-subscription=''
                onChange={e => {
                  dispatch(
                    setSignupApplication({
                      ...application,
                      marketingOptInIndicator: e.target.checked,
                    })
                  )
                }}
                isDisabled={isBusy}
              />
            </div>
          )}
        </FormSection>

        <FooterStyled>
          <Button
            type='button'
            isFullWidth
            disabled={!isEmailValid(application.email) || !isMobileValid(application.mobileNumber)}
            isLoading={isBusy}
            onClick={saveApplication}
          >
            Next
          </Button>
        </FooterStyled>
      </SignUpPageStyled>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
